import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { markdownToHtmlForSlack, markdownToSlackMrkdwn } from "../../lib/slack-utils";
import { StatusUpdateScope } from "tekkr-common/dist/model/project/chat";
import { ProjectPageHeader } from "../project/components/project-page-header";
import { useProject } from "../../data/project";
import { UpdateHistorySidebar } from "./components/update-history-sidebar";
import { UpdateDraftDialog, getInitialMessage } from "./components/update-draft-dialog";
import { useCreateProjectUpdateMutation } from "../../data/mutations/create-project-update";
import { useDeleteProjectUpdateMutation } from "../../data/mutations/delete-project-update";
import { ProjectUpdate } from "tekkr-common/dist/model/project/project-update";
import { TekkrMarkdown } from "../../components/shared/markdown/tekkr-markdown";
import { ChatSubpageApi } from "../project/chat/chat-subpage";
import { CopyButton } from "../../components/shared/copy-button";
import { ScopeSelector } from "./components/scope-selector";
import { getScopeLabel } from "./scope-utils";

function ViewUpdateContent({ update }: { update: ProjectUpdate }) {
    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                    Sent on{" "}
                    {DateTime.fromISO(update.sentAt).toLocaleString({
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                    })}
                    {" · "}
                    {getScopeLabel(update.scope)}
                </div>
                <div className="flex gap-2">
                    <CopyButton text={update.content} label="Copy Markdown" />
                    <CopyButton
                        text={update.content}
                        label="Copy for Slack"
                        transform={markdownToSlackMrkdwn}
                        htmlTransform={markdownToHtmlForSlack}
                    />
                </div>
            </div>
            <div className="rounded-lg border p-4">
                <TekkrMarkdown markdown={update.content} />
            </div>
        </div>
    );
}

export function ProjectUpdatesPage() {
    const { id: projectId } = useParams();
    const { project } = useProject();

    const [selectedUpdateId, setSelectedUpdateId] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draftScope, setDraftScope] = useState<StatusUpdateScope | null>(null);

    const chatRef = useRef<ChatSubpageApi>(null);

    const createUpdateMutation = useCreateProjectUpdateMutation({
        onSuccess: () => {
            setDialogOpen(false);
        },
    });

    const deleteUpdateMutation = useDeleteProjectUpdateMutation();

    if (!projectId) {
        throw new Error("projectId param is required!");
    }

    const updates: ProjectUpdate[] = project.updates ?? [];
    const selectedUpdate = selectedUpdateId ? updates.find((u) => u.id === selectedUpdateId) : null;

    // Compute last update date from updates
    const lastUpdateDate =
        updates.length > 0
            ? [...updates].sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0].sentAt
            : null;

    const handleStartDraft = (scope: StatusUpdateScope) => {
        setDraftScope(scope);
        setDialogOpen(true);
        setTimeout(() => {
            chatRef.current?.startChatWithMessage(getInitialMessage(scope), []);
        }, 100);
    };

    const handleMarkAsSent = (chatId: string, content: string) => {
        const scope = draftScope ?? { type: "days" as const, days: 7 };
        createUpdateMutation.mutate({
            projectId,
            chatId,
            scope,
            content,
        });
    };

    const handleDeleteUpdate = (updateId: string) => {
        deleteUpdateMutation.mutate({ projectId, updateId });
        if (selectedUpdateId === updateId) {
            setSelectedUpdateId(null);
        }
    };

    return (
        <div className="flex w-full max-w-[100vw] flex-1 flex-row overflow-hidden">
            <UpdateHistorySidebar
                updates={updates}
                selectedUpdateId={selectedUpdateId}
                onSelectUpdate={setSelectedUpdateId}
                onDeleteUpdate={handleDeleteUpdate}
                projectName={project.name}
            />
            <div className="flex w-full grow flex-col p-6">
                <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-6">
                    <ProjectPageHeader preheader="Status Updates" />

                    {selectedUpdate ? (
                        <ViewUpdateContent update={selectedUpdate} />
                    ) : (
                        <ScopeSelector
                            onStartDraft={handleStartDraft}
                            lastUpdateDate={lastUpdateDate}
                            projectCreatedAt={project.createdAt}
                        />
                    )}
                </div>
            </div>

            <UpdateDraftDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                scope={draftScope ?? { type: "days", days: 7 }}
                onMarkAsSent={handleMarkAsSent}
                chatRef={chatRef}
            />
        </div>
    );
}
